import { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Icon,
  USER_INTERFACE_ARROW_04_UP,
  USER_INTERFACE_ARROW_04_DOWN,
} from '@bwoty-web/styleguide';
import { classificationItemPropType } from '../../../utils/propTypes';
import {
  secondaryClassificationBadges,
  hotelProductBadges,
  conceptHotelBadges,
  hotelRatingBadges,
  temperatureBadge,
  temperaturePeriodBadge,
  locationBadges,
  flightTimeBadge,
  paxAdultsBadge,
  paxChildrenBadge,
  airportBadge,
  departureMonthBadges,
} from './badgeGetters';
import {
  handleSecondaryClassificationSelect,
  handleHotelProductSelect,
  handleConceptHotelSelect,
  handleRatingSelect,
  handleLocationSelect,
  handleTemperatureSelect,
  handleTemperaturePeriodSelect,
  handleFlightTimeSelect,
  handleNumberOfAdultsSelect,
  handleNumberOfChildrenSelect,
  handleDepartureSelect,
  handleDepartureMonthsSelect,
} from '../../../redux/actions/siteSettingsActions';
import FilterBadge from './filterBadge';
import './selectedFiltersPanel.scss';

const SelectedFiltersPanel = ({
  filters,
  classifications,
  hotelProducts,
  conceptHotels,
  departureAirports,
  onHandleSecondaryClassificationSelect,
  onHandleHotelProductSelect,
  onHandleConceptHotelSelect,
  onHandleRatingSelect,
  onHandleLocationSelect,
  onHandleTemperatureSelect,
  onHandleTemperaturePeriodSelect,
  onHandleFlightTimeSelect,
  onHandleNumberOfAdultsSelect,
  onHandleNumberOfChildrenSelect,
  onHandleDepartureSelect,
  onHandleDepartureMonthsSelect,
  labels,
}) => {
  const availableRawClassifications = useMemo(() => {
    let availableRaws = {};
    filters.selectedPrimaryClassifications.forEach(
      (selectedPrimaryClassification) => {
        availableRaws = {
          ...availableRaws,
          ...classifications[selectedPrimaryClassification].children,
        };
      },
    );
    return availableRaws;
  }, [filters.selectedSecondaryClassifications]);

  const getBadges = () => {
    const badges = [
      ...secondaryClassificationBadges(
        filters.selectedSecondaryClassifications,
        availableRawClassifications,
        onHandleSecondaryClassificationSelect,
        labels,
      ),
      ...hotelProductBadges(
        filters.selectedHotelProducts,
        hotelProducts,
        onHandleHotelProductSelect,
      ),
      ...conceptHotelBadges(
        filters.selectedConceptHotels,
        conceptHotels,
        onHandleConceptHotelSelect,
      ),
      ...hotelRatingBadges(
        filters.selectedRatings,
        onHandleRatingSelect,
        labels,
      ),
      ...locationBadges(
        filters.selectedLocations,
        onHandleLocationSelect,
        labels,
      ),
      ...temperatureBadge(
        filters.selectedTemperature,
        onHandleTemperatureSelect,
        labels,
      ),
      ...temperaturePeriodBadge(
        filters.selectedTemperaturePeriod,
        onHandleTemperaturePeriodSelect,
        labels,
      ),
      ...flightTimeBadge(
        filters.selectedFlightTime,
        onHandleFlightTimeSelect,
        labels,
      ),
      ...paxAdultsBadge(filters.pax, onHandleNumberOfAdultsSelect, labels),
      ...paxChildrenBadge(filters.pax, onHandleNumberOfChildrenSelect, labels),
      ...airportBadge(
        filters.pax,
        departureAirports,
        onHandleDepartureSelect,
        labels,
      ),
      ...departureMonthBadges(
        filters.selectedDepartureMonths,
        onHandleDepartureMonthsSelect,
        labels,
      ),
    ];

    return badges;
  };

  /*
    We want to limit the number of displayed filter-badges so that it always fits on a single row
    but display as many as possible. We do this by getting a calculated 
    total number of characters + number of filters per available space in pixels.

    We then compare this number to a defined threshold. The outcome is the number of items to display
    by default. This is not connected to when the screen is resized (due to performance),
    only calculated no page load + when the user changes selected classifications.
  */
  const dynamicSelectedFiltersCount = useMemo(() => {
    if (typeof window === 'undefined') return 0;

    // Doing a dom-check since ref is not defined during inital render
    const containerWidth = document.querySelector(
      '.vf-result-container',
    )?.offsetWidth;

    // Lower threshold === display more badges
    const mobileThreshold = 10.5;
    const threshHold = 8.5;
    // Didn't match 1:1 aginst containerWidth, desktop needed a bigger threshold
    const threshold = containerWidth >= 767 ? threshHold : mobileThreshold;

    // How much "space" each item gets besided the characters. Margin+padding + close-icon
    const itemSpacing = 8.5;
    const sizeCount = {
      characters: 0,
      items: 0,
    };

    const showAllLength = labels.resultListYourFiltersShowAll.length;
    const badges = getBadges();
    for (let i = 0; i < badges.length; i += 1) {
      sizeCount.characters += badges[i].displayName.length;
      sizeCount.items += 1;

      // We're always display a toggle button with a text saying "Show all" that differs in different languages (showAllLength)
      // showAllLength + current total of characters + current total number of items (current loop iterator) times the itemSpacing const
      const badgeSpace =
        showAllLength +
        sizeCount.characters +
        (sizeCount.items + 1) * itemSpacing;
      // Calculate available space per number of characters + extra space around each badge
      const containerWidthPerItemSpace = (containerWidth / badgeSpace).toFixed(
        2,
      );
      // then compare it to the defined threshold
      if (containerWidthPerItemSpace < threshold) {
        break;
      }
    }

    return sizeCount.items;
  }, [filters]);

  const INIT_DISPLAY_LIMIT = dynamicSelectedFiltersCount;
  const [displayLimit, setDisplayLimit] = useState(INIT_DISPLAY_LIMIT);

  const getTotalBadgeCount = () => {
    const badgeCount =
      filters.selectedSecondaryClassifications?.length +
      filters.selectedHotelProducts?.length +
      filters.selectedConceptHotels?.length +
      (filters.selectedRatings?.length || 0) +
      (filters.selectedLocations?.length || 0) +
      (filters.selectedTemperature ? 1 : 0) +
      (filters.selectedTemperaturePeriod ? 1 : 0) +
      (filters.selectedFlightTime ? 1 : 0) +
      1 + // pax adults, always show
      (filters.pax.selectedNumberOfChildren > 0 ? 1 : 0) +
      1 + // pax airport, always show
      (filters.selectedDepartureMonths?.length || 0);

    return badgeCount;
  };

  const displayShowAllSelectedFilters = () => {
    // return filters.selectedSecondaryClassifications.length > displayLimit;
    return getTotalBadgeCount() > displayLimit;
  };

  const displayHideAllSelectedFilters = () => {
    return (
      !displayShowAllSelectedFilters() &&
      getTotalBadgeCount() > INIT_DISPLAY_LIMIT
    );
  };

  const toggleButton = (label, icon, count) => {
    return (
      <div
        role="button"
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.keycode === 13) {
            setDisplayLimit(count);
          }
        }}
        onClick={() => setDisplayLimit(count)}
        className="vf-selected-filters-panel__badge vf-selected-filters-panel__badge__toggle"
      >
        <span className="vf-selected-filters-panel__badge__label">{label}</span>
        <Icon path={icon} className="vf-selected-filters-panel__badge__close" />
      </div>
    );
  };

  const handleIndicateToolpanel = () => {
    const toolpanel = document.querySelector('.tool-panel-container');
    if (toolpanel.className.includes('tool-panel-container__jump-indicate'))
      return;

    toolpanel.classList.add('tool-panel-container__jump-indicate');
    setTimeout(() => {
      toolpanel.classList.remove('tool-panel-container__jump-indicate');
    }, 500);
  };

  const RenderBadges = () => {
    return getBadges()
      .slice(0, displayLimit)
      .map((badge) => {
        const { displayName, id, canDelete = true, handleDelete } = badge;
        return (
          <FilterBadge
            key={id}
            displayName={displayName}
            canDelete={canDelete}
            handleIndicateFilters={handleIndicateToolpanel}
            handleDelete={handleDelete}
          />
        );
      });
  };

  return (
    <div className="vf-selected-filters-panel">
      <div
        className="vf-selected-filters-panel__label"
        role="button"
        onClick={handleIndicateToolpanel}
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.keycode === 13) {
            handleIndicateToolpanel();
          }
        }}
      >
        {labels.resultListYourFilters}:
      </div>

      <RenderBadges />

      {displayShowAllSelectedFilters() &&
        toggleButton(
          labels.resultListYourFiltersShowAll,
          USER_INTERFACE_ARROW_04_DOWN,
          getTotalBadgeCount(),
        )}
      {displayHideAllSelectedFilters() &&
        toggleButton(
          labels.resultListYourFiltersShowLess,
          USER_INTERFACE_ARROW_04_UP,
          INIT_DISPLAY_LIMIT,
        )}
    </div>
  );
};

SelectedFiltersPanel.propTypes = {
  filters: PropTypes.shape({
    selectedPrimaryClassifications: PropTypes.arrayOf(PropTypes.string)
      .isRequired,
    selectedSecondaryClassifications: PropTypes.arrayOf(PropTypes.string)
      .isRequired,
    selectedHotelProducts: PropTypes.arrayOf(PropTypes.string),
    selectedConceptHotels: PropTypes.arrayOf(PropTypes.string),
    selectedLocations: PropTypes.arrayOf(PropTypes.string),
    selectedTemperature: PropTypes.string,
    selectedTemperaturePeriod: PropTypes.string,
    selectedFlightTime: PropTypes.string,
    selectedPreference: PropTypes.string,
    selectedRatings: PropTypes.arrayOf(PropTypes.string),
    pax: PropTypes.shape({}),
    selectedDepartureMonths: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  classifications: PropTypes.shape(classificationItemPropType).isRequired,
  hotelProducts: PropTypes.shape({}).isRequired,
  conceptHotels: PropTypes.shape({}).isRequired,
  departureAirports: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  onHandleSecondaryClassificationSelect: PropTypes.func.isRequired,
  onHandleHotelProductSelect: PropTypes.func.isRequired,
  onHandleLocationSelect: PropTypes.func.isRequired,
  onHandleTemperatureSelect: PropTypes.func.isRequired,
  onHandleTemperaturePeriodSelect: PropTypes.func.isRequired,
  onHandleFlightTimeSelect: PropTypes.func.isRequired,
  onHandleConceptHotelSelect: PropTypes.func.isRequired,
  onHandleRatingSelect: PropTypes.func.isRequired,
  onHandleNumberOfAdultsSelect: PropTypes.func.isRequired,
  onHandleNumberOfChildrenSelect: PropTypes.func.isRequired,
  onHandleDepartureSelect: PropTypes.func.isRequired,
  onHandleDepartureMonthsSelect: PropTypes.func.isRequired,
  labels: PropTypes.shape({
    resultListYourFilters: PropTypes.string,
    resultListYourFiltersShowAll: PropTypes.string,
    resultListYourFiltersShowLess: PropTypes.string,
    flightTime: PropTypes.string,
    accommodationFilterRating: PropTypes.string,
    paxPickerAdult: PropTypes.string,
    paxPickerChild: PropTypes.string,
    paxPickerDepartureLocation: PropTypes.string,
    hotelDeparture: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = ({ siteSettings, attributeCollection }) => {
  const labels = {
    resultListYourFilters: attributeCollection.resultListYourFilters,
    resultListYourFiltersShowAll:
      attributeCollection.resultListYourFiltersShowAll,
    resultListYourFiltersShowLess:
      attributeCollection.resultListYourFiltersShowLess,
    ASIA: attributeCollection.locationPicker_ASIA,
    AFRICA: attributeCollection.locationPicker_AFRICA,
    CANARY_ISLANDS: attributeCollection.locationPicker_CANARY_ISLANDS,
    CARIBEAN_CENTRAL_AMERICA:
      attributeCollection.locationPicker_CARIBEAN_CENTRAL_AMERICA,
    EUROPE: attributeCollection.locationPicker_EUROPE,
    NORTH_AMERICA: attributeCollection.locationPicker_NORTH_AMERICA,
    SOUTH_AMERICA: attributeCollection.locationPicker_SOUTH_AMERICA,
    temp_cool: attributeCollection.traitTemperature_cool,
    temp_medium: attributeCollection.traitTemperature_medium,
    temp_warm: attributeCollection.traitTemperature_warm,
    spring: attributeCollection.traitTemperature_spring,
    summer: attributeCollection.traitTemperature_summer,
    autumn: attributeCollection.traitTemperature_autumn,
    winter: attributeCollection.traitTemperature_winter,
    flightTime: attributeCollection.preferenceView_FT,
    flight_short: attributeCollection.traitFlightTime_short,
    flight_medium: attributeCollection.traitFlightTime_medium,
    flight_long: attributeCollection.traitFlightTime_long,
    accommodationFilterRating: attributeCollection.accommodationFilterRating,
    paxPickerAdult: attributeCollection.paxPickerAdult,
    paxPickerChild: attributeCollection.paxPickerChild,
    paxPickerDepartureLocation: attributeCollection.paxPickerDepartureLocation,
    hotelDeparture: attributeCollection.hotelDeparture,
  };
  return {
    classifications: siteSettings.classifications,
    hotelProducts: siteSettings.hotelProducts,
    conceptHotels: siteSettings.conceptHotels,
    departureAirports: siteSettings.departureAirports,
    filters: siteSettings.filters,
    labels,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onHandleSecondaryClassificationSelect: (classification) => {
      dispatch(handleSecondaryClassificationSelect(classification));
    },
    onHandleHotelProductSelect: (hotelProduct) => {
      dispatch(handleHotelProductSelect(hotelProduct));
    },
    onHandleConceptHotelSelect: (conceptHotel) => {
      dispatch(handleConceptHotelSelect(conceptHotel));
    },
    onHandleRatingSelect: (rating) => {
      dispatch(handleRatingSelect(rating));
    },
    onHandleLocationSelect: (location) => {
      dispatch(handleLocationSelect(location));
    },
    onHandleTemperatureSelect: (temperature) => {
      dispatch(handleTemperatureSelect(temperature));
    },
    onHandleTemperaturePeriodSelect: (temperaturePeriod) => {
      dispatch(handleTemperaturePeriodSelect(temperaturePeriod));
    },
    onHandleFlightTimeSelect: (flightTime) => {
      dispatch(handleFlightTimeSelect(flightTime));
    },
    onHandleNumberOfAdultsSelect: (adults) => {
      dispatch(handleNumberOfAdultsSelect(adults));
    },
    onHandleNumberOfChildrenSelect: (children) => {
      dispatch(handleNumberOfChildrenSelect(children));
    },
    onHandleDepartureSelect: (airport, airportCaDict) => {
      dispatch(handleDepartureSelect(airport, airportCaDict));
    },
    onHandleDepartureMonthsSelect: (month) => {
      dispatch(handleDepartureMonthsSelect(month));
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelectedFiltersPanel);
